<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { MarEmptyState, MarLoadingSpinner, MarModals, MarPage, MarPageHeader } from '@nimling/components-nimtech'
import type { Page, PageView } from '@/types'
import { getLoginURL, getObjectSettings, getPage, getPageViews } from '@/lib/api'
import { useViewsStore } from '@/stores/views'
import { useVariablesStore } from '@/stores/variables'
import { useThemesStore } from '@/stores/themes'
import PageGrid from '@/components/PageGrid.vue'
import VariableBar from '@/components/VariableBar.vue'
import { useRangeQuery, useVariableQuery } from '@/lib/query'

const route = useRoute()
const router = useRouter()

const viewsStore = useViewsStore()
const variablesStore = useVariablesStore()
const themesStore = useThemesStore()

const page = ref<Page | null>(null)
const placements = ref<PageView[]>([])
const loading = ref(true)
const live = ref(true)
const rootEl = ref<HTMLElement | null>(null)
const pageEl = ref<{ scrollEl?: HTMLElement | null } | null>(null)

useRangeQuery()
useVariableQuery(() => (typeof route.params.id === 'string' ? route.params.id : null))

const hasVariables = computed(() => (page.value?.variables?.length ?? 0) > 0)

onMounted(async () => {
  await router.isReady()
  const pageId = typeof route.params.id === 'string' ? route.params.id : ''
  try {
    const [p, views, setting] = await Promise.all([getPage(pageId), getPageViews(pageId), getObjectSettings('pages', pageId), themesStore.putPageTheme(rootEl.value as HTMLElement, pageId)])
    live.value = setting.value.guest_stream !== false
    const readable = await Promise.all([...new Set(views.map((v) => v.view_id))].map((id) => viewsStore.getView(id).then(() => id, () => null)))
    variablesStore.putVariables(p.id, Array.isArray(p.variables) ? p.variables : [])
    document.title = `${p.name} · Hvergel`
    placements.value = views.filter((v) => readable.includes(v.view_id))
    page.value = p
  } catch {
    window.location.assign(getLoginURL())
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <MarModals />
  <div ref="rootEl" class="root" :data-page-bare="page?.layout?.bare || undefined">
    <MarPage ref="pageEl">
      <template #header>
        <MarPageHeader :title="page?.name ?? 'Page'" :description="page?.description ?? undefined">
          <template v-if="hasVariables && page" #content-view>
            <VariableBar :page="page" />
          </template>
        </MarPageHeader>
      </template>
      <template #content>
        <MarLoadingSpinner v-if="loading" mode="page" label="Loading" />
        <MarEmptyState
          v-else-if="!page"
          icon="triangle-alert"
          severity="danger"
          title="Page not available"
        />
        <PageGrid v-else class="guest-grid" :page="page" :page-views="placements" :live="live" :scroller="pageEl?.scrollEl" />
      </template>
    </MarPage>
  </div>
</template>
<style scoped>
.root {
  position: absolute;
  inset: 0;
  background-color: var(--mar-surface-100);
}

.guest-grid {
  flex: 1 0 auto;
}
</style>
